import { useState, useMemo } from "react";
import { AlertTriangle, Bell, ChevronRight, Menu, RefreshCw } from "lucide-react";
import type { AdminTalent, AdminOrg } from "./data";
import { useAdminData } from "./hooks/useAdminData";
import { useIsMobile } from "./hooks/useIsMobile";
import logoBlack from "./assets/Logo_Black.svg";
import { ConfirmModal, ConfirmState } from "./components/feedback/ConfirmModal";
import { ToastList, useToast } from "./components/feedback/Toast";
import { NotificationDrawer } from "./components/layout/NotificationDrawer";
import { Sidebar, getNavGroups } from "./components/layout/Sidebar";
import { AdminUsersView } from "./features/admins/AdminUsersView";
import { AnalyticsView } from "./features/analytics/AnalyticsView";
import { ApplicationsView } from "./features/applications/ApplicationsView";
import { AuditView } from "./features/audit/AuditView";
import { CommunicationsView } from "./features/communications/CommunicationsView";
import { ContentView } from "./features/content/ContentView";
import { DashboardView } from "./features/dashboard/DashboardView";
import { FeeManagementView } from "./features/fees/FeeManagementView";
import { ModerationView } from "./features/moderation/ModerationView";
import { OrgDetailView } from "./features/orgs/OrgDetailView";
import { OrgsView } from "./features/orgs/OrgsView";
import { PaymentsView } from "./features/payments/PaymentsView";
import { PostingsView } from "./features/postings/PostingsView";
import { SettingsView } from "./features/settings/SettingsView";
import { TalentDetailView } from "./features/talents/TalentDetailView";
import { TalentsView } from "./features/talents/TalentsView";
import { VerificationView } from "./features/verification/VerificationView";
import { VerificationFormsView } from "./features/verificationForms/VerificationFormsView";
import { CORAL, F, T } from "./theme/tokens";

interface AdminAppProps {
  onLogout: () => void;
  admin?: { name: string; email: string; role: string };
}

export default function AdminApp({ onLogout, admin }: AdminAppProps) {
  const { ADMIN_NOTIFICATIONS, VERIFICATION_QUEUE, ADMIN_REPORTS, loading, error } = useAdminData();
  const [activeView, setActiveView] = useState("dashboard");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [talentDetail, setTalentDetail] = useState<AdminTalent | null>(null);
  const [orgDetail, setOrgDetail] = useState<AdminOrg | null>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  // Below 900px the sidebar is a slide-in drawer instead of a permanent rail.
  const isMobile = useIsMobile(900);
  const [navOpen, setNavOpen] = useState(false);
  const unreadCount = ADMIN_NOTIFICATIONS.filter((n) => !n.read).length;
  const navGroups = useMemo(
    () => getNavGroups(VERIFICATION_QUEUE.filter((v) => !v.assignedTo).length, ADMIN_REPORTS.filter((r) => r.status === "Open").length),
    [VERIFICATION_QUEUE, ADMIN_REPORTS]
  );
  const [confirmState, setConfirmState] = useState<ConfirmState>({ open: false, title: "", body: "", destructive: false, confirmLabel: "Confirm", onConfirm: () => { } });
  const { toasts, add: addToast } = useToast();

  function openConfirm(opts: Omit<ConfirmState, "open">) {
    setConfirmState({ ...opts, open: true });
  }

  function handleNav(id: string) {
    setActiveView(id);
    setTalentDetail(null);
    setOrgDetail(null);
    setNotifOpen(false);
    setNavOpen(false);
  }

  const sidebarActive = talentDetail ? "talents" : orgDetail ? "orgs" : activeView === "verification-forms" ? "settings" : activeView;

  function renderView() {
    if (talentDetail) return <TalentDetailView talent={talentDetail} onBack={() => setTalentDetail(null)} addToast={addToast} confirm={openConfirm} />;
    if (orgDetail) return <OrgDetailView org={orgDetail} onBack={() => setOrgDetail(null)} addToast={addToast} confirm={openConfirm} />;

    switch (activeView) {
      case "dashboard": return <DashboardView onNav={handleNav} addToast={addToast} />;
      case "analytics": return <AnalyticsView />;
      case "talents": return <TalentsView onDetail={(t) => { setTalentDetail(t); setOrgDetail(null); }} addToast={addToast} />;
      case "orgs": return <OrgsView onDetail={(o) => { setOrgDetail(o); setTalentDetail(null); }} addToast={addToast} />;
      case "postings": return <PostingsView addToast={addToast} />;
      case "applications": return <ApplicationsView />;
      case "payments": return <PaymentsView addToast={addToast} />;
      case "verification": return <VerificationView addToast={addToast} />;
      case "moderation": return <ModerationView addToast={addToast} />;
      case "communications": return <CommunicationsView addToast={addToast} />;
      case "fees": return <FeeManagementView addToast={addToast} />;
      case "category":
      case "content": return <ContentView addToast={addToast} />;
      case "admins": return <AdminUsersView addToast={addToast} />;
      case "audit": return <AuditView />;
      case "settings": return <SettingsView addToast={addToast} onOpenVerificationForms={() => setActiveView("verification-forms")} />;
      case "verification-forms": return <VerificationFormsView addToast={addToast} confirm={openConfirm} onBack={() => setActiveView("settings")} />;
      default: return <DashboardView onNav={handleNav} addToast={addToast} />;
    }
  }

  return (
    <div style={{ display: "flex", height: "100dvh", overflow: "hidden", fontFamily: F, background: T.bg }}>
      {!isMobile && (
        <Sidebar active={sidebarActive} onChange={handleNav} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((v) => !v)} onLogout={onLogout} admin={admin} navGroups={navGroups} />
      )}
      {isMobile && navOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1500 }} role="dialog" aria-modal="true" aria-label="Navigation">
          <div onClick={() => setNavOpen(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} />
          <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "min(280px, 85vw)", boxShadow: "8px 0 32px rgba(0,0,0,0.3)" }}>
            <Sidebar mobile active={sidebarActive} onChange={handleNav} collapsed={false} onToggle={() => setNavOpen(false)} onLogout={onLogout} admin={admin} navGroups={navGroups} />
          </div>
        </div>
      )}

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Top bar */}
        <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}`, padding: isMobile ? "0 16px" : "0 28px", height: isMobile ? 60 : 52, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, zIndex: 10 }}>
          {isMobile ? (
            <button onClick={() => handleNav("dashboard")} aria-label="Dashboard" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex" }}>
              <img src={logoBlack} alt="Donatella" height={18} width={Math.round((840 / 117) * 18)} style={{ display: "block" }} draggable={false} />
            </button>
          ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: T.sub, fontFamily: F, minWidth: 0, flex: 1, overflow: "hidden", whiteSpace: "nowrap" }}>
            <span style={{ color: CORAL, fontWeight: 600 }}>Admin</span>
            {talentDetail && <><ChevronRight size={12} /><span>Talent</span><ChevronRight size={12} /><span style={{ color: T.text, overflow: "hidden", textOverflow: "ellipsis" }}>{talentDetail.name}</span></>}
            {orgDetail && <><ChevronRight size={12} /><span>Organisations</span><ChevronRight size={12} /><span style={{ color: T.text, overflow: "hidden", textOverflow: "ellipsis" }}>{orgDetail.name}</span></>}
            {!talentDetail && !orgDetail && <><ChevronRight size={12} /><span style={{ color: T.text, textTransform: "capitalize", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{activeView === "verification-forms" ? "Settings" : activeView.replace("-", " ")}</span>{activeView === "verification-forms" && <><ChevronRight size={12} /><span style={{ color: T.text }}>Verification Forms</span></>}</>}
          </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
            <button onClick={() => setNotifOpen((v) => !v)} title="Notifications" style={{ position: "relative", background: notifOpen ? T.s2 : "none", border: notifOpen ? `1px solid ${T.border}` : "none", cursor: "pointer", color: T.sub, padding: "5px 7px", borderRadius: "7px", display: "flex", alignItems: "center" }}>
              <Bell size={16} />
              {unreadCount > 0 && <span style={{ position: "absolute", top: 2, right: 2, width: 8, height: 8, borderRadius: "50%", background: CORAL, border: `2px solid ${T.surface}` }} />}
            </button>
            <div style={{ width: 1, height: 18, background: T.border }} />
            <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: CORAL, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: "9px", fontWeight: 700, color: "#fff", fontFamily: F }}>SA</span>
              </div>
              {!isMobile && <div style={{ fontSize: "11px", color: T.sub, fontFamily: F }}>Super Admin</div>}
            </div>
            {isMobile && (
              <button onClick={() => setNavOpen(true)} aria-label="Open menu" aria-expanded={navOpen} style={{ display: "flex", background: "none", border: "none", cursor: "pointer", color: T.text, padding: "6px", marginRight: "-6px" }}>
                <Menu size={22} />
              </button>
            )}
          </div>
        </div>

        {/* Main content */}
        <main style={{ flex: 1, overflowY: "auto", padding: isMobile ? "18px 14px 32px" : "28px 32px", scrollbarWidth: "none" }}>
          {/* A failed fetch used to render as an empty table, which is
              indistinguishable from "this platform has no talent yet". */}
          {error && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#fee2e2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: "10px", padding: "10px 14px", marginBottom: "18px", fontSize: "12px", fontFamily: F }}>
              <AlertTriangle size={14} />
              <span><strong>Couldn't load platform data.</strong> {error} — the figures below may be incomplete.</span>
            </div>
          )}
          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: T.muted, fontSize: "12px", fontFamily: F, marginBottom: "18px" }}>
              <RefreshCw size={13} /> Loading platform data…
            </div>
          )}
          {renderView()}
        </main>
      </div>

      <NotificationDrawer open={notifOpen} onClose={() => setNotifOpen(false)} onNav={handleNav} />
      <ConfirmModal state={confirmState} onClose={() => setConfirmState((s) => ({ ...s, open: false }))} addToast={addToast} />
      <ToastList toasts={toasts} />
    </div>
  );
}
